import { useState, useEffect, useRef, useMemo } from "react";
import CustomTooltip from '../common/CustomTooltip';
import { HiOutlineCode, HiOutlineViewGrid, HiOutlineSearch, HiOutlineX, HiOutlineClipboardCopy, HiOutlineClipboardCheck } from "react-icons/hi";
import toast from "react-hot-toast";

interface StateMappingComponentProps {
    localMappings: Record<string, any>;
    setLocalMappings: (mappings: Record<string, any>) => void;
    localMappingsOrder?: Record<string, any>;
    isJsonView: boolean;
    setIsJsonView: (isJsonView: boolean) => void;
    jsonInput: string;
    setJsonInput: (jsonInput: string) => void;
    isLoading?: boolean;
    baselineMappings?: Record<string, any>;
    baselineOrder?: Record<string, any>;
    onDirtyChange?: (dirty: boolean) => void;
}

const StateMappingComponent = ({
    localMappings,
    setLocalMappings,
    localMappingsOrder = {},
    isJsonView,
    setIsJsonView,
    jsonInput,
    setJsonInput,
    isLoading = false,
    baselineMappings,
    baselineOrder,
    onDirtyChange
}: StateMappingComponentProps) => {
    const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
    const [currentTeamInProjectIndex, setCurrentTeamInProjectIndex] = useState(0);
    const [focusedInput, setFocusedInput] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [copiedStateMappings, setCopiedStateMappings] = useState<Record<string, any> | null>(null);

    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const teamScrollContainerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Search functionality - filter projects and teams based on search query
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) {
            return { projects: localMappings, hasResults: true, matchingTeams: {} };
        }

        const query = searchQuery.toLowerCase().trim();
        const filtered: Record<string, any> = {};
        const matchingTeams: Record<string, string[]> = {};
        let hasResults = false;

        Object.entries(localMappings).forEach(([projectKey, projectData]) => {
            const projectMatches = projectKey.toLowerCase().includes(query);
            const teamMatches: string[] = [];

            // Check teams in this project
            Object.entries(projectData as Record<string, any>).forEach(([teamKey, teamData]) => {
                const teamMatchesQuery = teamKey.toLowerCase().includes(query);
                
                if (teamMatchesQuery) {
                    teamMatches.push(teamKey);
                }
            });

            // If project matches or has matching teams, include ALL teams from the project
            if (projectMatches || teamMatches.length > 0) {
                filtered[projectKey] = projectData; // Include ALL teams
                matchingTeams[projectKey] = teamMatches;
                hasResults = true;
            }
        });

        return { projects: filtered, hasResults, matchingTeams };
    }, [localMappings, searchQuery]);

    const filteredProjectKeysString = useMemo(() => {
        return Object.keys(filteredData.projects).sort().join(',');
    }, [filteredData.projects]);

    // Memoized project and team data to avoid repetition
    const projectsData = useMemo(() => {
        const dataToUse = searchQuery ? filteredData.projects : localMappings;
        const projectKeys = Object.keys(dataToUse);
        const currentProjectKey = projectKeys[currentTeamIndex];
        const currentProject = currentProjectKey ? dataToUse[currentProjectKey] || {} : {};
        const teamKeys = Object.keys(currentProject);
        const currentTeamKey = teamKeys[currentTeamInProjectIndex];

        return {
            projectKeys,
            currentProjectKey,
            currentProject,
            teamKeys,
            currentTeamKey,
            isLastProject: currentTeamIndex >= projectKeys.length - 1,
            hasProjects: projectKeys.length > 0,
            hasTeams: teamKeys.length > 0
        };
    }, [localMappings, filteredData.projects, currentTeamIndex, currentTeamInProjectIndex, searchQuery]);

    const focusMatchingInput = () => {
        const currentProjectKey = Object.keys(filteredData.projects)[currentTeamIndex];

        if (currentProjectKey && filteredData.matchingTeams[currentProjectKey]?.length > 0) {
            const projectTeams = Object.keys(filteredData.projects[currentProjectKey]);
            const firstMatchingTeam = filteredData.matchingTeams[currentProjectKey][0];
            const matchingTeamIndex = projectTeams.indexOf(firstMatchingTeam);
            if (matchingTeamIndex !== -1) {
                setCurrentTeamInProjectIndex(matchingTeamIndex);
            }
        } else {
            setCurrentTeamInProjectIndex(0);
        }
    };

    // Reset indices when search changes and auto-select matching team
    useEffect(() => {
        if (searchQuery) {
            setCurrentTeamIndex(0);
            focusMatchingInput()
        }
    }, [searchQuery, filteredProjectKeysString]);

    useEffect(() => {
        focusMatchingInput();
    }, [currentTeamIndex]);

    useEffect(() => {
        if (teamScrollContainerRef.current) {
            const selectedTeamElement = document.getElementById(`team-${currentTeamInProjectIndex}`);
            if (selectedTeamElement) {
                // Calculate the scroll position to center the selected team
                const container = teamScrollContainerRef.current;
                const containerWidth = container.offsetWidth;
                const teamElementLeft = selectedTeamElement.offsetLeft;
                const teamElementWidth = selectedTeamElement.offsetWidth;
                
                // Center the selected team in the container
                const scrollPosition = teamElementLeft - (containerWidth / 2) + (teamElementWidth / 2);
                
                // Smooth scroll to the position
                container.scrollTo({
                    left: scrollPosition,
                    behavior: 'smooth'
                });
            }
        }
    }, [currentTeamInProjectIndex]);

    // Maintain focus after state updates
    useEffect(() => {
        if (focusedInput && inputRefs.current[focusedInput]) {
            inputRefs.current[focusedInput]?.focus();
        }
    }, [localMappings, focusedInput]);

    const handlePreviousTeam = () => {
        if (currentTeamIndex > 0) {
            setCurrentTeamIndex(prev => prev - 1);
        }
    };

    const handleNextTeam = () => {
        if (currentTeamIndex < projectsData.projectKeys.length - 1) {
            setCurrentTeamIndex(prev => prev + 1);
        }
    };

    const handlePercentageChange = (projectKey: string, teamKey: string, stateName: string, value: string, inputId: string) => {
        // Set the currently focused input
        setFocusedInput(inputId);
        
        // Create a deep copy of the mappings
        const updatedMappings: Record<string, any> = { ...localMappings };
        
        const existingProject = updatedMappings[projectKey] || {};
        updatedMappings[projectKey] = { ...existingProject };
        
        const existingTeam = updatedMappings[projectKey][teamKey] || {};
        updatedMappings[projectKey][teamKey] = { ...existingTeam };
        
        // Allow only numeric values or an empty string
        if (!/^\d*$/.test(value)) {
            return;
        }

        let finalValue = value;
        if (value !== '') {
            const numericValue = parseInt(value, 10);
            if (numericValue > 100) {
                finalValue = '100';
                toast.error('Percentage cannot be greater than 100');
            }
        }
        
        updatedMappings[projectKey][teamKey][stateName] = finalValue === '' ? '' : `${finalValue}%`;
        
        setLocalMappings(updatedMappings);
    };

    const handleInputFocus = (inputId: string) => {
        setFocusedInput(inputId);
    };

    const handleInputBlur = () => {
        setFocusedInput(null);
    };

    const handlePreviousTeamInProject = () => {
        if (currentTeamInProjectIndex > 0) {
            setCurrentTeamInProjectIndex(currentTeamInProjectIndex - 1);
        }
    };

    const handleNextTeamInProject = () => {
        if (currentTeamInProjectIndex < projectsData.teamKeys.length - 1) {
            setCurrentTeamInProjectIndex(prev => prev + 1);
        }
    };

    const handleNextProject = () => {
        if (currentTeamIndex < projectsData.projectKeys.length - 1) {
            setCurrentTeamIndex(prev => prev + 1);
            setCurrentTeamInProjectIndex(0); // Reset to first team of next project
        }
    };

    const handleToggleView = () => {
        if (isJsonView) {
            // Switching from JSON to UI view
            try {
                const parsedMappings = JSON.parse(jsonInput);
                
                // Validate that parsedMappings is an object
                if (typeof parsedMappings !== 'object' || parsedMappings === null) {
                    throw new Error('Column mappings must be a valid object');
                }
                
                setLocalMappings(parsedMappings);
                setIsJsonView(false);
            } catch (error) {
                console.error('Error parsing JSON:', error);
                toast.error('Invalid JSON format. Please fix before switching to UI view.');
            }
        } else {
            // Switching from UI to JSON view
            setIsJsonView(true);
        }
    };

    const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setJsonInput(e.target.value);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleClearSearch = (e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setSearchQuery('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const handleCopyMappings = async () => {
        try {
            if (!projectsData.currentProjectKey || !projectsData.teamKeys[currentTeamInProjectIndex]) {
                return;
            }
            const currentTeamKey = projectsData.teamKeys[currentTeamInProjectIndex];
            const stateData = projectsData.currentProject[currentTeamKey] || {};
            const stateDataCopy = JSON.parse(JSON.stringify(stateData));
            setCopiedStateMappings(stateDataCopy);
            const serialized = JSON.stringify(stateDataCopy, null, 2);
            if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(serialized);
            }
            toast.success('Column mappings copied');
        } catch (error) {
            console.error('Failed to copy mappings:', error);
            toast.error('Unable to copy mappings');
        }
    };

    const handlePasteMappings = () => {
        if (!copiedStateMappings) {
            toast.error('Copy mappings first before pasting');
            return;
        }
        const currentProjectKey = projectsData.currentProjectKey;
        const currentTeamKey = projectsData.teamKeys[currentTeamInProjectIndex];
        if (!currentProjectKey || !currentTeamKey) {
            toast.error('Select a project and team to paste mappings');
            return;
        }

        const targetStateData = projectsData.currentProject[currentTeamKey] || {};
        const copiedKeys = Object.keys(copiedStateMappings);
        const targetKeys = Object.keys(targetStateData);

        if (copiedKeys.length !== targetKeys.length) {
            toast.error('Copied mappings do not match the selected team structure');
            return;
        }

        const mismatchedKey = targetKeys.find(key => !copiedKeys.includes(key));
        if (mismatchedKey) {
            toast.error('Copied mappings do not match the selected team structure');
            return;
        }

        const updatedMappings = { ...localMappings };
        if (!updatedMappings[currentProjectKey]) {
            updatedMappings[currentProjectKey] = {};
        }
        if (!updatedMappings[currentProjectKey][currentTeamKey]) {
            updatedMappings[currentProjectKey][currentTeamKey] = {};
        }

        copiedKeys.forEach((stateName) => {
            updatedMappings[currentProjectKey][currentTeamKey][stateName] = copiedStateMappings[stateName];
        });

        setLocalMappings(updatedMappings);
        toast.success('State mappings pasted successfully');
    };

    const stableStringify = (value: any): string => {
        if (value === null || typeof value !== 'object') return JSON.stringify(value);
        if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
        const keys = Object.keys(value).sort();
        return `{${keys.map(k => JSON.stringify(k)+':'+stableStringify(value[k])).join(',')}}`;
    };

    useEffect(() => {
        if (!onDirtyChange) return;
        if (!baselineMappings) {
            onDirtyChange(false);
            return;
        }
        try {
            const currentMappings = isJsonView ? JSON.parse(jsonInput || '{}') : localMappings;
            const equalMaps = stableStringify(currentMappings) === stableStringify(baselineMappings);
            const hasOrder = baselineOrder && Object.keys(baselineOrder || {}).length > 0;
            const equalOrder = hasOrder
                ? stableStringify(localMappingsOrder || {}) === stableStringify(baselineOrder || {})
                : true;
            onDirtyChange(!(equalMaps && equalOrder));
        } catch {
            onDirtyChange(true);
        }
    }, [isJsonView, jsonInput, localMappings, localMappingsOrder, baselineMappings, baselineOrder]);

    const renderNavigationButton = (
        onClick: () => void,
        label: string,
        disabled = false,
        isNext = false,
        tooltipTitle?: string
    ) => {
        const btn = (
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                className={`flex items-center justify-center w-6 h-6 rounded-md transition-all ${
                    disabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                        : 'bg-primary-light/20 text-primary-light hover:bg-primary-light/30 dark:bg-primary-dark/20 dark:text-primary-dark dark:hover:bg-primary-dark/30'
                }`}
            >
                <span className="sr-only">{label}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isNext ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    )}
                </svg>
            </button>
        );
        return (
            <CustomTooltip title={tooltipTitle || label} placement="top">
                {btn}
            </CustomTooltip>
        );
    };

    // Skeleton Loader Component
    const SkeletonLoader = () => (
        <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            {/* Top Section Skeleton */}
            <div className="bg-gradient-to-r from-tertiary-light to-secondary-light dark:from-tertiary-dark dark:to-secondary-dark px-6 py-4 border-b border-gray-100 dark:border-gray-700 h-20">
                <div className="flex items-center justify-between h-full">
                    {/* Left - Search Skeleton */}
                    <div className="flex items-center w-40">
                        <div className="flex items-center bg-secondary-light dark:bg-secondary-dark border border-gray-200 dark:border-gray-600 rounded-lg">
                            <div className="flex items-center px-3 py-2">
                                <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                            </div>
                            <div className="px-2 py-2 w-32">
                                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                            </div>
                        </div>
                    </div>

                    {/* Center - Project Navigation Skeleton */}
                    <div className="flex items-center justify-center flex-1">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-20 h-3 bg-gray-300 dark:bg-gray-600 rounded mb-2 animate-pulse"></div>
                            <div className="w-32 h-6 bg-gray-300 dark:bg-gray-600 rounded mb-1 animate-pulse"></div>
                            
                            {/* Compact Navigation with Pagination Skeleton */}
                            <div className="flex items-center justify-center space-x-1">
                                <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
                                <div className="w-16 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                                <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
                            </div>
                        </div>
                    </div>

                    {/* Right - Toggle Button Skeleton */}
                    <div className="flex items-center justify-end w-40">
                        <div className="w-20 h-8 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
                    </div>
                </div>
            </div>

            {/* Team Selection Skeleton */}
            <div className="bg-gradient-to-r from-hover-light to-tertiary-light dark:from-hover-dark dark:to-tertiary-dark p-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex flex-col">
                    <div className="w-12 h-3 bg-gray-300 dark:bg-gray-600 rounded mb-2 animate-pulse"></div>
                    <div className="flex items-center space-x-2">
                        {[1, 2, 3, 4].map((index) => (
                            <div 
                                key={index}
                                className="w-20 h-8 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* States Mapping Content Skeleton */}
            <div className="p-5 bg-secondary-light dark:bg-secondary-dark">
                <div className="flex items-center justify-between mb-4">
                    <div className="w-32 h-6 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                    <div className="w-40 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                </div>
                
                <div className="space-y-3 min-h-[280px] max-h-[280px]">
                    {[1, 2, 3, 4].map((index) => (
                        <div 
                            key={index}
                            className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-primary-light/5 to-hover-light dark:from-primary-dark/5 dark:to-hover-dark border border-primary-light/10 dark:border-gray-700"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-7 h-7 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></div>
                                <div className="w-24 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                            </div>
                            <div className="flex items-center">
                                <div className="w-16 h-8 bg-gray-300 dark:bg-gray-600 rounded-md mr-1 animate-pulse"></div>
                                <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer Controls Skeleton */}
            <div className="border-t border-gray-200 dark:border-gray-700 py-4 px-5 bg-white dark:bg-hover-dark">
                <div className="w-full flex justify-between items-center">
                    <div className="w-24 h-10 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
                    <div className="w-16 h-6 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></div>
                    <div className="w-24 h-10 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
                </div>
            </div>
        </div>
    );

    // Show skeleton loader when loading
    if (isLoading) {
        return <SkeletonLoader />;
    }

    return (
        <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden h-[600px] flex flex-col">
            {/* Top Section - Fixed height with consistent layout */}
            <div className="bg-gradient-to-r from-tertiary-light to-secondary-light dark:from-tertiary-dark dark:to-secondary-dark px-6 py-4 border-b border-gray-100 dark:border-gray-700 h-20 flex-shrink-0">
                <div className="flex items-center justify-between h-full">
                    {/* Left - Search Controls or Empty Space */}
                    <div className="flex items-center w-40">
                        {!isJsonView && (
                            <div className="flex items-center bg-secondary-light dark:bg-secondary-dark border border-gray-200 dark:border-gray-600 rounded-lg">
                                <div className="flex items-center px-3 py-2 text-gray-400 dark:text-gray-500">
                                    <HiOutlineSearch className="w-4 h-4" />
                                </div>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    placeholder="Search..."
                                    className="px-2 py-2 text-sm bg-transparent border-none outline-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 w-32"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchQuery('');
                                            searchInputRef.current?.focus();
                                        }}
                                        className="flex items-center justify-center px-2 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200 cursor-pointer z-10"
                                    >
                                        <HiOutlineX className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Center - Navigation or JSON Header */}
                    <div className="flex items-center justify-center flex-1">
                        {!isJsonView ? (
                            <div className="flex flex-col items-center text-center">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {searchQuery ? 'FILTERED PROJECT' : 'PROJECT'}
                                </span>
                                <h2 className="text-xl font-bold bg-gradient-to-r from-primary-light via-primary-dark to-primary-hover bg-clip-text text-transparent mb-0.5">
                                    {projectsData.hasProjects 
                                        ? projectsData.currentProjectKey
                                        : " - "}
                                </h2>
                                
                                {/* Compact Navigation with Pagination */}
                                <div className="flex items-center justify-center space-x-1">
                                    <button 
                                        onClick={handlePreviousTeam}
                                        disabled={currentTeamIndex === 0 || !projectsData.hasProjects}
                                        className={`flex items-center justify-center w-5 h-5 rounded-md ${
                                            currentTeamIndex === 0 || !projectsData.hasProjects
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                                            : 'bg-primary-light/20 text-primary-light hover:bg-primary-light/30 dark:bg-primary-dark/20 dark:text-primary-dark dark:hover:bg-primary-dark/30'
                                        } transition-all`}
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                                        </svg>
                                    </button>
                                    
                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300 px-3">
                                        {projectsData.hasProjects 
                                            ? `${currentTeamIndex + 1} of ${projectsData.projectKeys.length}` 
                                            : "0 of 0"}
                                    </span>
                                    
                                    <button 
                                        onClick={handleNextTeam}
                                        disabled={projectsData.isLastProject || !projectsData.hasProjects}
                                        className={`flex items-center justify-center w-5 h-5 rounded-md ${
                                            projectsData.isLastProject || !projectsData.hasProjects
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                                            : 'bg-primary-light/20 text-primary-light hover:bg-primary-light/30 dark:bg-primary-dark/20 dark:text-primary-dark dark:hover:bg-primary-dark/30'
                                        } transition-all`}
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">JSON</span>
                                <h2 className="text-lg font-bold text-gray-800 dark:text-white">Column Mappings Configuration</h2>
                            </div>
                        )}
                    </div>

                    {/* Right - JSON View Toggle */}
                    <div className="flex items-center justify-end w-40">
                        <button 
                            onClick={handleToggleView}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary-light/20 hover:bg-primary-light/30 text-primary-light dark:text-primary-dark border border-primary-light/30 transition-all duration-200"
                        >
                            {isJsonView ? (
                                <>
                                    <HiOutlineViewGrid className="w-4 h-4" />
                                    <span>UI View</span>
                                </>
                            ) : (
                                <>
                                    <HiOutlineCode className="w-4 h-4" />
                                    <span>JSON View</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {isJsonView ? (
                /* JSON Editor View - Fixed height */
                <div className="flex-1 p-4 bg-secondary-light dark:bg-secondary-dark overflow-hidden flex flex-col">
                    <div className="mb-3 text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                        Paste your JSON configuration below. Ensure it follows the correct format for column mappings.
                    </div>
                    <textarea
                        value={jsonInput}
                        onChange={handleJsonChange}
                        className="flex-1 w-full p-3 rounded-md font-mono text-sm border border-gray-200 dark:border-gray-700 focus:ring-0 focus:outline-none focus:border-primary-light dark:focus:border-primary-dark bg-hover-light dark:bg-hover-dark text-gray-900 dark:text-gray-100 overflow-auto transition-all duration-200 resize-none"
                        placeholder="Paste your JSON configuration here..."
                    />
                </div>
            ) : (
                /* UI State Mapping View - Fixed height structure */
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* No Search Results State - Fixed height */}
                    {searchQuery && !filteredData.hasResults && (
                        <div className="flex-1 flex items-center justify-center bg-secondary-light dark:bg-secondary-dark">
                            <div className="text-center p-8 rounded-xl bg-hover-light dark:bg-hover-dark border border-gray-100/60 dark:border-gray-700/40 max-w-md">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 mb-4">
                                    <HiOutlineSearch className="w-8 h-8" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">No Results Found</h3>
                                <p className="text-gray-500 dark:text-gray-400 mb-4">
                                    No projects or teams match "<span className="font-semibold text-primary-light dark:text-primary-dark">{searchQuery}</span>"
                                </p>
                                <button
                                    onClick={handleClearSearch}
                                    className="px-4 py-2 text-sm font-medium text-primary-light dark:text-primary-dark hover:text-primary-hover dark:hover:text-blue-300 hover:bg-primary-light/10 dark:hover:bg-primary-dark/10 rounded-lg transition-all duration-200 border border-primary-light/30 dark:border-primary-dark/30"
                                >
                                    Clear Search
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Team Selection - Fixed height */}
                    {(!searchQuery || filteredData.hasResults) && (
                        <div className="bg-gradient-to-r from-hover-light to-tertiary-light dark:from-hover-dark dark:to-tertiary-dark p-4 border-b border-gray-100 dark:border-gray-700 h-30 flex-shrink-0">
                            <div className="flex flex-col h-full">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider flex-shrink-0">Team</span>
                                <div className="relative overflow-x-auto flex-1 min-h-0">
                                    <div 
                                        ref={teamScrollContainerRef}
                                        className="flex items-center space-x-2 h-full overflow-x-auto"
                                    >
                                        {(() => {
                                            return projectsData.teamKeys.map((teamName, idx) => {
                                                const isCurrentTeam = idx === currentTeamInProjectIndex;
                                                
                                                return (
                                                    <button 
                                                        id={`team-${idx}`}
                                                        key={teamName} 
                                                        className={`px-3 py-2 rounded-md transition-all text-sm ${
                                                            isCurrentTeam
                                                            ? 'bg-gradient-to-r from-primary-light to-primary-dark text-white font-medium shadow-md' 
                                                            : 'bg-secondary-light dark:bg-secondary-dark text-gray-700 dark:text-gray-300 hover:bg-hover-light dark:hover:bg-hover-dark border border-gray-200 dark:border-gray-700 hover:border-primary-light/50 dark:hover:border-primary-dark/50'
                                                        } whitespace-nowrap flex-shrink-0`}
                                                        onClick={() => setCurrentTeamInProjectIndex(idx)}
                                                    >
                                                        {teamName}
                                                    </button>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* States Mapping Content - Fixed height */}
                    {(!searchQuery || filteredData.hasResults) && (
                        <div className="flex-1 p-5 bg-secondary-light dark:bg-secondary-dark overflow-hidden flex flex-col min-h-0">
                        {(() => {
                            try {
                                if (!projectsData.currentProjectKey) return (
                                    <div className="flex-1 flex items-center justify-center">
                                        <div className="text-center text-gray-500 dark:text-gray-400">
                                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 mb-3">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                                                </svg>
                                            </div>
                                            <p>Select a project to configure column mappings</p>
                                        </div>
                                    </div>
                                );
                                
                                if (!projectsData.hasTeams) return (
                                    <div className="flex-1 flex items-center justify-center">
                                        <div className="text-center text-gray-500 dark:text-gray-400">
                                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 mb-3">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                                </svg>
                                            </div>
                                            <p>This project doesn't have any teams configured</p>
                                        </div>
                                    </div>
                                );

                                const currentTeamKey = projectsData.teamKeys[currentTeamInProjectIndex];
                                const stateData = projectsData.currentProject[currentTeamKey] || {};
                                
                                return (
                                    <>
                                        <div className="flex items-center justify-between mb-4 flex-shrink-0">
                                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Column Mappings</h3>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={handleCopyMappings}
                                                    disabled={!projectsData.hasTeams}
                                                    className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                                                        projectsData.hasTeams
                                                            ? 'text-primary-light dark:text-primary-dark border-primary-light/40 dark:border-primary-dark/40 hover:bg-primary-light/10 dark:hover:bg-primary-dark/10'
                                                            : 'text-gray-400 border-gray-200 dark:border-gray-700 cursor-not-allowed'
                                                    }`}
                                                >
                                                    <HiOutlineClipboardCopy className="w-4 h-4" />
                                                    Copy states
                                                </button>
                                                <button
                                                    onClick={handlePasteMappings}
                                                    disabled={!projectsData.hasTeams}
                                                    className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                                                        projectsData.hasTeams
                                                            ? 'text-primary-light dark:text-primary-dark border-primary-light/40 dark:border-primary-dark/40 hover:bg-primary-light/10 dark:hover:bg-primary-dark/10'
                                                            : 'text-gray-400 border-gray-200 dark:border-gray-700 cursor-not-allowed'
                                                    }`}
                                                >
                                                    <HiOutlineClipboardCheck className="w-4 h-4" />
                                                    Paste states
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div className="flex-1 min-h-0 overflow-y-auto">
                                            <div className="space-y-3">
                                                {(() => {
                                                    const stateEntries = Object.entries(stateData);
                                                    
                                                    if (stateEntries.length === 0) {
                                                        return null; // Will be handled below
                                                    }

                                                    // Get the current team's order mapping from localMappingsOrder
                                                    const currentTeamOrder = localMappingsOrder[projectsData.currentProjectKey]?.[currentTeamKey] || {};
                                                    
                                                    // Check if we have any order data for this team
                                                    const hasOrderData = Object.keys(currentTeamOrder).length > 0;
                                                    
                                                    // Sort states based on order, or preserve original order if no ordering data
                                                    const sortedStateEntries = hasOrderData 
                                                        ? stateEntries.sort(([stateNameA], [stateNameB]) => {
                                                            const orderA = currentTeamOrder[stateNameA];
                                                            const orderB = currentTeamOrder[stateNameB];
                                                            
                                                            // If both have order numbers, sort by order
                                                            if (orderA !== undefined && orderB !== undefined) {
                                                                const numA = typeof orderA === 'string' ? parseInt(orderA, 10) : orderA;
                                                                const numB = typeof orderB === 'string' ? parseInt(orderB, 10) : orderB;
                                                                return numA - numB;
                                                            }
                                                            
                                                            // If only one has order, prioritize it
                                                            if (orderA !== undefined && orderB === undefined) return -1;
                                                            if (orderA === undefined && orderB !== undefined) return 1;
                                                            
                                                            // If neither have order, sort alphabetically
                                                            return stateNameA.localeCompare(stateNameB);
                                                        })
                                                        : stateEntries; // Preserve original order if no ordering data

                                                    return sortedStateEntries.map(([stateName, completionValue], stateIndex) => {
                                                        const inputId = `${projectsData.currentProjectKey}-${currentTeamKey}-${stateName}`;
                                                        
                                                        return (
                                                            <div 
                                                                key={stateIndex} 
                                                                className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-primary-light/5 to-hover-light dark:from-primary-dark/5 dark:to-hover-dark hover:from-primary-light/10 hover:to-hover-light dark:hover:from-primary-dark/10 dark:hover:to-hover-dark transition-all duration-200 group border border-primary-light/10 dark:border-gray-700 hover:border-primary-light/30 dark:hover:border-primary-dark/30"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-primary-light/80 to-primary-dark/90 text-white text-xs font-medium dark:from-primary-dark/70 dark:to-primary-hover/80 shadow-sm flex-shrink-0">
                                                                        {stateIndex + 1}
                                                                    </div>
                                                                    <span className="font-medium text-gray-700 dark:text-gray-200 group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors duration-200">
                                                                        {stateName}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center flex-shrink-0">
                                                                    <input
                                                                        type="text"
                                                                        ref={el => inputRefs.current[inputId] = el}
                                                                        value={String(completionValue).replace(/%/g, '')}
                                                                        onChange={(e) => 
                                                                            handlePercentageChange(
                                                                                projectsData.currentProjectKey,
                                                                                currentTeamKey,
                                                                                stateName,
                                                                                e.target.value,
                                                                                inputId
                                                                            )
                                                                        }
                                                                        onFocus={() => handleInputFocus(inputId)}
                                                                        onBlur={handleInputBlur}
                                                                        className="w-16 px-2 py-1 mr-1 text-center rounded-md border border-primary-light/30 dark:border-primary-dark/30 focus:ring-0 focus:outline-none focus:border-primary-light dark:focus:border-primary-dark dark:bg-secondary-dark text-gray-900 dark:text-white text-sm transition-all duration-200"
                                                                        maxLength={3}
                                                                        placeholder="0"
                                                                    />
                                                                    <div className="px-2 py-1 rounded-lg bg-gradient-to-r from-primary-light/90 to-primary-dark/80 text-white text-sm font-medium transition-all duration-200 shadow-sm">
                                                                        %
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                                {Object.entries(stateData).length === 0 && (
                                                    <div className="flex items-center justify-center h-full min-h-[200px]">
                                                                                                            <div className="text-center p-6 rounded-xl bg-hover-light dark:bg-hover-dark border border-gray-100/60 dark:border-gray-700/40">
                                                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark mb-3">
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                            </svg>
                                                        </div>
                                                        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">No Column Mappings Found</h3>
                                                       
                                                        <p className="text-sm text-gray-400 dark:text-gray-500">
                                                            Sync your integration to import project states
                                                        </p>
                                                    </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                );
                            } catch (err) {
                                console.error('Error rendering team states:', err);
                                return (
                                    <div className="flex-1 flex items-center justify-center">
                                        <div className="text-center text-gray-500 dark:text-gray-400">
                                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 mb-3">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                                                </svg>
                                            </div>
                                            <p>Unable to display states for this project/team.</p>
                                        </div>
                                    </div>
                                );
                            }
                        })()}
                        </div>
                    )}

                    {/* State Mapping Footer Controls - Fixed height */}
                    {(!searchQuery || filteredData.hasResults) && (
                        <div className="border-t border-gray-200 dark:border-gray-700 py-4 px-5 bg-hover-light dark:bg-hover-dark h-20 flex-shrink-0">
                            {(() => {
                                if (!projectsData.currentProjectKey) {
                                    return (
                                    <div className="w-full flex justify-center items-center h-full">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">Select a project to continue</span>
                                    </div>
                                );
                                }

                                const totalTeams = projectsData.teamKeys.length || 0;
                                const isLastTeamInProject = totalTeams === 0 || currentTeamInProjectIndex >= totalTeams - 1;

                                const nextConfig = (() => {
                                    if (totalTeams === 0) {
                                        return { onClick: () => {}, label: 'Next Team', disabled: true, isNext: true };
                                    }

                                if (!isLastTeamInProject) {
                                        return { onClick: handleNextTeamInProject, label: 'Next Team', disabled: false, isNext: true };
                                    }

                                    if (!projectsData.isLastProject) {
                                        return { onClick: handleNextProject, label: 'Next Project', disabled: false, isNext: true };
                                    }

                                    return { onClick: () => {}, label: 'Next Team', disabled: true, isNext: true };
                                })();
                                
                                return (
                                    <div className="w-full flex justify-center items-center h-full">
                                        <div className="flex items-center space-x-2">
                                        {renderNavigationButton(
                                            handlePreviousTeamInProject,
                                            "Previous Team",
                                                currentTeamInProjectIndex === 0 || totalTeams === 0,
                                                false
                                        )}
                                        
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300 px-3 py-2">
                                                {totalTeams > 0 ? `${currentTeamInProjectIndex + 1} of ${totalTeams}` : '0 of 0'}
                                            </span>

                                            {renderNavigationButton(
                                                nextConfig.onClick,
                                                nextConfig.label,
                                                nextConfig.disabled,
                                                nextConfig.isNext
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StateMappingComponent; 
